import { CircularProgress, Switch } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeUser } from "../../types";
import { useToast } from "../../context/toast-context";
import { UserService } from "../../services/user-service";

interface ToggleEmployeeOnLeaveProps {
  employee: EmployeeUser;
  onOnLeaveChange: (userId: number, branchId: number, onLeave: boolean) => void;
  size?: "default" | "small";
}

export function ToggleEmployeeOnLeave({
  employee,
  onOnLeaveChange,
  size = "default",
}: ToggleEmployeeOnLeaveProps) {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const userService = useMemo(() => new UserService(), []);

  const handleToggle = async () => {
    if (loading) return;
    const nextOnLeave = !employee.onLeave;
    setLoading(true);
    await userService.toggleEmployeeOnLeave(
      {
        UserId: employee.userId,
        BranchId: employee.branchId,
      },
      () => {
        onOnLeaveChange(employee.userId, employee.branchId, nextOnLeave);
        setLoading(false);
        showToast(
          employee.onLeave
            ? t("employee.status.activated") || "Empleado activado"
            : t("employee.status.deactivated") || "Empleado inactivo",
          "success"
        );
      },
      (error) => {
        showToast(error, "error");
        setLoading(false);
      }
    );
  };

  const isPresent = !employee.onLeave;

  return (
    <div className="relative inline-flex items-center">
      <Switch
        checked={isPresent}
        onChange={handleToggle}
        disabled={loading}
        size={size === "small" ? "small" : "medium"}
        title={
          isPresent
            ? t("employee.status.setAbsent") || "Poner ausente"
            : t("employee.status.setPresent") || "Poner presente"
        }
        inputProps={{
          "aria-label": isPresent
            ? t("employee.status.setAbsent") || "Poner ausente"
            : t("employee.status.setPresent") || "Poner presente",
        }}
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            bgcolor: "#10B981",
            opacity: 1,
          },
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: "#10B981",
          },
          "& .MuiSwitch-track": {
            bgcolor: "#EF4444",
            opacity: 1,
          },
          "& .MuiSwitch-thumb": {
            bgcolor: isPresent ? "#10B981" : "#EF4444",
          },
        }}
      />
      {loading && (
        <CircularProgress
          size={size === "small" ? 12 : 16}
          sx={{
            position: "absolute",
            right: -4,
            color: isPresent ? "#10B981" : "#EF4444",
          }}
        />
      )}
    </div>
  );
}
